import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BookOpen, Search, Users, MessageCircle, Bookmark, CalendarDays, Home, LogOut, Sparkles, Heart, Star, HeartHandshake, Share2 } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import InviteDialog from "@/components/InviteDialog";

const navItems = [
  { to: "/home", key: "home", Icon: Home },
  { to: "/read", key: "read", Icon: BookOpen },
  { to: "/search", key: "search", Icon: Search },
  { to: "/community", key: "community", Icon: MessageCircle },
  { to: "/prayers", key: "prayers", Icon: HeartHandshake },
  { to: "/plans", key: "plans", Icon: CalendarDays },
  { to: "/studies", key: "studies", Icon: Sparkles },
  { to: "/bookmarks", key: "bookmarks", Icon: Bookmark },
];

export default function Layout({ children }) {
  const { user, lang, setLang, logout } = useApp();
  const navigate = useNavigate();
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text-primary)] pb-24 md:pb-0">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-[var(--border)]/60 bg-[var(--bg)]/60 backdrop-blur-2xl backdrop-saturate-150">
        <div className="max-w-6xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <button
            data-testid="brand-home-btn"
            onClick={() => navigate("/home")}
            className="flex items-center gap-2 group"
          >
            <img src="/icon-app.jpg" alt="FaithCycle" className="w-8 h-8 rounded-full ring-1 ring-[var(--border)] group-hover:scale-105 transition-transform duration-300" />
            <span className="font-serif text-xl tracking-tight">FaithCycle</span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map(({ to, key, Icon }) => (
              <NavLink
                key={key}
                to={to}
                data-testid={`nav-${key}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm transition-colors duration-200 flex items-center gap-2 ${
                    isActive
                      ? "bg-[var(--surface)] text-[var(--text-primary)]"
                      : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                <span>{t(`nav.${key}`, lang)}</span>
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="invite-btn"
              onClick={() => setInviteOpen(true)}
              className="hidden sm:flex items-center gap-1.5 h-9 px-4 rounded-full text-xs uppercase tracking-widest border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors duration-200"
            >
              <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>{lang === "es" ? "Invitar" : "Invite"}</span>
            </button>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger
                data-testid="language-select"
                className="w-[80px] h-9 bg-transparent border-[var(--border)] rounded-full text-xs uppercase tracking-widest"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--surface-elevated)]">
                <SelectItem value="en" data-testid="lang-en">EN</SelectItem>
                <SelectItem value="es" data-testid="lang-es">ES</SelectItem>
              </SelectContent>
            </Select>

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-btn" className="rounded-full outline-none">
                    <Avatar className="w-9 h-9 border border-[var(--border)]">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="bg-[var(--surface)] text-sm">
                        {user.name?.[0]?.toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-[var(--surface-elevated)] border-[var(--border)]"
                >
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-sm flex items-center gap-1.5">
                      {user.name}
                      {user.is_supporter && (
                        <Star className="w-3 h-3 text-[var(--accent)] fill-[var(--accent)]" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="text-xs text-[var(--text-secondary)]">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="profile-menu-item"
                    onClick={() => navigate("/profile")}
                  >
                    {t("nav.profile", lang)}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="invite-menu-item"
                    onClick={() => setInviteOpen(true)}
                  >
                    <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {lang === "es" ? "Invitar amigos" : "Invite friends"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="support-menu-item"
                    onClick={() => navigate("/support")}
                  >
                    <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {t("nav.support", lang)}
                  </DropdownMenuItem>
                  {user.is_supporter && (
                    <DropdownMenuItem
                      data-testid="early-access-menu-item"
                      onClick={() => navigate("/early-access")}
                    >
                      <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      {lang === "es" ? "Acceso anticipado" : "Early access"}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="logout-btn" onClick={logout}>
                    <LogOut className="w-4 h-4 mr-2" /> {t("signOut", lang)}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="app-content max-w-6xl mx-auto px-6 md:px-8 py-8 md:py-12">{children}</main>

      {/* Mobile Bottom Nav — iPhone dock style (floating pill) */}
      <nav className="md:hidden fixed bottom-3 left-3 right-3 z-40 rounded-3xl border border-[var(--border)]/60 bg-[var(--bg)]/70 backdrop-blur-2xl backdrop-saturate-150 shadow-[0_8px_32px_rgba(20,15,10,0.15)]">
        <div className="flex items-center gap-1 px-3 py-2 overflow-x-auto no-scrollbar">
          {navItems.map(({ to, key, Icon }) => (
            <NavLink
              key={key}
              to={to}
              data-testid={`mobile-nav-${key}`}
              className={({ isActive }) =>
                `flex-shrink-0 flex flex-col items-center gap-1 px-3 py-1.5 rounded-2xl text-[10px] uppercase tracking-widest transition-all duration-200 min-w-[58px] ${
                  isActive
                    ? "text-[var(--accent)] bg-[var(--highlight)]/50"
                    : "text-[var(--text-secondary)]"
                }`
              }
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{t(`nav.${key}`, lang)}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}

export const Btn = ({ className = "", children, ...props }) => (
  <Button
    className={`rounded-full font-normal ${className}`}
    {...props}
  >
    {children}
  </Button>
);
